"use client";
import React, { useState, useEffect, useRef, ForwardedRef } from "react";
import HTMLFlipBook from "react-pageflip";
import { Document, Page, pdfjs } from "react-pdf";
import type { PDFDocumentProxy } from "pdfjs-dist";
import { trackEvent } from "@/lib/meta-pixel";
// import dummypdf from '../../public/pdf/investor_charter_pdf.pdf'

// import workerSrc from "pdfjs-dist/build/pdf.worker.js?url";
// pdfjs.GlobalWorkerOptions.workerSrc = workerSrc;
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

interface PagesProps {
  pageNumber: number;
  width: number;
  // height: number;
}

const Pages = React.forwardRef<HTMLDivElement, PagesProps>(
  ({ pageNumber, width }, ref: ForwardedRef<HTMLDivElement>) => {
    return (
      <div
        ref={ref}
        className="relative flex h-full items-center bg-white dark:bg-gray-900 rounded-md shadow-xl"
      >
        <Page
          pageNumber={pageNumber}
          scale={1}
          width={width - 30}
          renderAnnotationLayer={false}
          renderTextLayer={false}
        />
      </div>
    );
  }
);
Pages.displayName = "Pages";

interface MyBookProps {
  fileUrl: string;
}


export default function MyBook({ fileUrl }: MyBookProps) {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const bookRef = useRef<React.ComponentRef<typeof HTMLFlipBook>>(null);
  const [usePortraitMode, setUsePortraitMode] = useState(false);

  const [size, setSize] = useState<{ height: number; width: number }>({
    height: 0,
    width: 0,
  });

  const updateBookSize = () => {
    const isMobile = window.innerWidth <= 768;

    const width = isMobile ? window.innerWidth * 0.9 : window.innerWidth * 0.29;
    const height = isMobile ? window.innerHeight * 0.75 : window.innerHeight * 0.95;//height of window

    setSize({ width, height });
    setUsePortraitMode(isMobile);
  };


  // const updateBookSize = () => {
  //   const isMobile = window.innerWidth <= 768;

  //   const width = isMobile ? window.innerWidth * 0.9 : window.innerWidth * 0.3;
  //   const height = isMobile ? window.innerHeight * 0.7 : window.innerHeight * 0.85;

  //   setSize({ width, height });
  //   setUsePortraitMode(isMobile);

  //   console.log("Updated book size:", { width, height });
  // };

  useEffect(() => {
    updateBookSize();
    window.addEventListener("resize", updateBookSize);
    return () => window.removeEventListener("resize", updateBookSize);
  }, []);

  useEffect(() => {
    const fetchPdf = async () => {
      try {
        console.log("Fetching PDF file from:", fileUrl);
        const response = await fetch(fileUrl, {
          mode: 'cors',
          credentials: 'include'
        }); const blob = await response.blob();
        console.log("PDF fetched successfully, Blob size:", blob.size);
        setPdfBlob(blob); // This updates the state
      } catch (err) {
        console.error("❌ Failed to load PDF:", err);
      }
      // const response = await fetch(dummypdf);
      // const blob = await response.blob();
      // setPdfBlob(blob); // This updates the state
    };

    if (fileUrl) {
      fetchPdf();
    }

    updateBookSize();
    window.addEventListener("resize", updateBookSize);

    return () => window.removeEventListener("resize", updateBookSize);
  }, [fileUrl]);

  useEffect(() => {
    if (pdfBlob) {
      const objectUrl = URL.createObjectURL(pdfBlob);
      setPdfUrl(objectUrl);
      console.log("PDF object URL:", objectUrl); // Log the generated URL for debugging
    }
  }, [pdfBlob]);

  useEffect(() => {
    if (pdfUrl) {
      console.log("PDF URL state updated:", pdfUrl);
    }
  }, [pdfUrl]); // Log after pdfUrl state has been updated

  const onDocumentLoadSuccess = ({ numPages }: PDFDocumentProxy) => {
    console.log(`Document loaded successfully, number of pages: ${numPages}`);
    setNumPages(numPages);
    const contentName = decodeURIComponent(fileUrl.split("/").pop() || "Digital Brochure");
    trackEvent("ViewContent", { content_name: contentName, content_category: "brochure" });
  };

  const handlePageFlip = (e: { data: number }) => {
    console.log(`Flipped to page: ${e.data}`);
    setCurrentPage(e.data);
  };

  console.log("Rendering PDF with file URL:", fileUrl);
  console.log("Current PDF Blob:", pdfBlob);
  console.log("Number of Pages:", numPages);
  console.log("Generated PDF URL:", pdfUrl);

  useEffect(() => {
    if (pdfBlob) {
      console.log("PDF blob has been updated, triggering re-render.");
    }
  }, [pdfBlob]);

  return (
    <div className="relative w-full h-full bg-gray-950" onClick={(e) => e.stopPropagation()}>
      {/* Only render the Document once pdfUrl is available */}
      {pdfUrl ? (
        <Document
          file={pdfUrl} // Use the URL created from the Blob
          onLoadSuccess={onDocumentLoadSuccess}
          loading={
            <div className="flex justify-center items-center h-96">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
            </div>
          }
        >
          {numPages && (
            <HTMLFlipBook
              ref={bookRef}
              width={size.width}
              height={size.height}
              size="stretch"
              minWidth={300}
              maxWidth={1000}
              minHeight={400}
              maxHeight={2000}
              drawShadow
              showCover={false}
              mobileScrollSupport
              usePortrait={usePortraitMode}
              autoSize
              onFlip={handlePageFlip}
              className="shadow-2xl rounded-xl"
              style={{
                maxWidth: "100%",
                maxHeight: "100%",
              }}
              startPage={0}
              flippingTime={600}
              startZIndex={0}
              maxShadowOpacity={0.5}
              disableFlipByClick={false}
              clickEventForward={true}
              useMouseEvents={true}
              swipeDistance={30}
              showPageCorners={true}
            >
              {Array.from({ length: numPages }, (_, index) => (
                <Pages
                  key={`page-${index + 1}-${fileUrl}`}
                  width={size.width - (size.width > 680 ? 60 : 0)}
                  pageNumber={index + 1}
                />
              ))}
            </HTMLFlipBook>

          )}
        </Document>
      ) : (
        <div className="flex justify-center items-center h-96">
          <p className="text-gray-500">Loading PDF...</p>
        </div>
      )}

      {numPages && (
        <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 bg-gray-800/70 px-4 py-2 rounded-full shadow-md text-sm text-gray-200 backdrop-blur-md">
          Page {currentPage + 1} of {numPages}
        </div>
      )}
    </div>
  );
}
